import React, { useEffect, useRef, useState } from 'react';
import { Toaster } from 'sonner';
import { LanguageToggle } from './original/shared/components/LanguageToggle';
import { AdminLink } from './original/shared/components/AdminLink';
import { DeckNavigation } from './original/shared/components/DeckNavigation';
import { TranslationProvider, useTranslation } from './original/shared/contexts/TranslationContext';
import { Slide1 } from './original/decks/brazilian-rewards/components/Slide1';
import { Slide2 } from './original/decks/brazilian-rewards/components/Slide2';
import { Slide3 } from './original/decks/brazilian-rewards/components/Slide3';
import { Slide4 } from './original/decks/brazilian-rewards/components/Slide4';
import { Slide5 } from './original/decks/brazilian-rewards/components/Slide5';
import { Slide6 } from './original/decks/brazilian-rewards/components/Slide6';
import { Slide7 } from './original/decks/brazilian-rewards/components/Slide7';
import { Slide8 } from './original/decks/brazilian-rewards/components/Slide8';
import { Slide9 } from './original/decks/brazilian-rewards/components/Slide9';
import { Slide10 } from './original/decks/brazilian-rewards/components/Slide10';
import { EmissorDeck } from './original/decks/emissor/EmissorDeck';
import EzERPDeck from './original/decks/ezerp/EzERPDeck';
import EzERPPartnersDeck from './original/decks/ezerp-partners/EzERPPartnersDeck';
import { FiscalEmissionDeck } from './original/decks/fiscal-emission/FiscalEmissionDeck';
import { FiscalEmissionV2Deck } from './original/decks/fiscal-emission-v2/FiscalEmissionV2Deck';
import EmissorCRMDeck from './original/decks/emissor-crm/EmissorCRMDeck';
import EmissorCRMDeepDeck from './original/decks/emissor-crm/EmissorCRMDeepDeck';
import './OriginalPresentationDecks.css';

const BRAZILIAN_REWARDS_TOTAL = 10;

function BrazilianRewardsDeckInner({ lang, setLang }) {
  const [active, setActive] = useState(0);
  const deckRef = useRef(null);
  const { translations } = useTranslation();
  const t = translations && translations.s1
    ? translations
    : { s1: {}, s2: {}, s3: {}, s4: {}, s5: {}, s6: {}, s7: {}, s8: {}, s9: {}, s10: { labels: {} } };

  useEffect(() => {
    localStorage.setItem('br_lang', lang);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const jumpTo = (idx) => {
    const el = deckRef.current?.querySelector(`[data-slide-index="${idx}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        jumpTo(Math.min(BRAZILIAN_REWARDS_TOTAL - 1, active + 1));
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        jumpTo(Math.max(0, active - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="App relative theme-br" data-testid="original-brazilian-rewards-deck">
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: 'rgba(15,15,15,0.95)',
            color: '#fafafa',
            border: '1px solid rgba(212,175,55,0.3)',
            backdropFilter: 'blur(12px)',
            fontFamily: 'Manrope, sans-serif'
          }
        }}
      />

      <LanguageToggle lang={lang} setLang={setLang} />
      <AdminLink />
      <DeckNavigation count={BRAZILIAN_REWARDS_TOTAL} active={active} onJump={jumpTo} variant="dots" />

      <main ref={deckRef} className="deck no-scrollbar" data-testid="presentation-deck">
        <Slide1 t={t.s1} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide2 t={t.s2} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide3 t={t.s3} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide4 t={t.s4} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide5 t={t.s5} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide6 t={t.s6} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide7 t={t.s7} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide8 t={t.s8} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide9 t={t.s9} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} />
        <Slide10 t={t.s10} total={BRAZILIAN_REWARDS_TOTAL} onActive={setActive} lang={lang} />
      </main>
    </div>
  );
}

function BrazilianRewardsDeck() {
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('br_lang');
      if (stored === 'pt' || stored === 'en' || stored === 'es') return stored;
      const nav = (navigator.language || 'en').toLowerCase();
      if (nav.startsWith('es')) return 'es';
      return nav.startsWith('pt') ? 'pt' : 'en';
    }
    return 'en';
  });

  return (
    <TranslationProvider deck="brazilian-rewards" lang={lang}>
      <BrazilianRewardsDeckInner lang={lang} setLang={setLang} />
    </TranslationProvider>
  );
}

function IframeDeckInner({ src, title, lang, setLang }) {
  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden', position: 'relative' }}>
      {/* Embedded presentation — 100% width for correct font scaling, extra height clips bottom badge */}
      <iframe
        src={src}
        title={title}
        style={{
          width: '100%',
          height: 'calc(100% + 60px)',
          border: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1
        }}
      />

      {/* EzERP overlay controls */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          pointerEvents: 'none'
        }}
      >
        <div style={{ pointerEvents: 'auto' }}>
          <LanguageToggle lang={lang} setLang={setLang} />
        </div>
        <div style={{ pointerEvents: 'auto' }}>
          <AdminLink />
        </div>
      </div>
    </div>
  );
}

export function IframeDeck({ src, title, deckId }) {
  const [lang, setLang] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(`${deckId}_lang`);
      if (stored === 'pt' || stored === 'en' || stored === 'es') return stored;
      const nav = (navigator.language || 'en').toLowerCase();
      if (nav.startsWith('es')) return 'es';
      return nav.startsWith('pt') ? 'pt' : 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem(`${deckId}_lang`, lang);
    document.documentElement.setAttribute('lang', lang);
  }, [deckId, lang]);

  return (
    <IframeDeckInner src={src} title={title} lang={lang} setLang={setLang} />
  );
}

export function OriginalPresentationDeck({ sourceDeck }) {
  let deck = <EzERPDeck />;

  if (sourceDeck === 'ezerp-partners') deck = <EzERPPartnersDeck />;
  if (sourceDeck === 'emissor') deck = <EmissorDeck />;
  if (sourceDeck === 'brazilian-rewards') deck = <BrazilianRewardsDeck />;
  if (sourceDeck === 'full-stack-fiscal') deck = <IframeDeck src="https://gamma.app/embed/foehrhja9jecyv1?mode=doc" title="The Full-Stack Fiscal Emission Platform" deckId="full-stack-fiscal" />;
  if (sourceDeck === 'process-map') deck = <IframeDeck src="https://gamma.app/embed/x7onb9aoe8mdevs?mode=doc" title="EzERP 3PL DC - Process Map" deckId="process-map" />;
  if (sourceDeck === 'fiscal-emission') deck = <FiscalEmissionDeck />;
  if (sourceDeck === 'fiscal-emission-v2') deck = <FiscalEmissionV2Deck />;
  if (sourceDeck === 'emissor-crm') deck = <EmissorCRMDeck />;
  if (sourceDeck === 'emissor-crm-deep') deck = <EmissorCRMDeepDeck />;

  return (
    <div className="presentation-original-shell">
      {deck}
    </div>
  );
}
